import React, { FC, useEffect, useMemo, useState } from 'react';
import { FormGroup, Input, Label } from 'reactstrap';
import {
  DealerArea,
  DealerFullModel,
  dealerStatusList,
  DealerStatusTypes,
} from '../../../../../shared/dealer';
import { ProductAddonModel, ProductModel, ProductVisibility } from '../../../../../shared/orders';
import { useTranslation } from '../../../../translations';
import {
  capitalize,
  countBy,
  displayName,
  distinct,
  Fetcher,
  setElementInArray,
  useFetcher,
} from '../../../../utils';

type ApplicationType = DealerStatusTypes | 'paid' | 'unpaid';
const applicationType: ApplicationType[] = [
  ...dealerStatusList.map((t) => t.key),
  'paid',
  'unpaid',
];

interface DealerSelectorProps {
  readonly selectionChanged: (userIds: number[]) => void;
}

interface Filters {
  tableTypes: number[] | null;
  areaIds: number[];
  addonIds: number[] | null;
  status: ApplicationType[];
}

function filterDealers(dealers: DealerFullModel[], filter: Filters): DealerFullModel[] {
  const result: DealerFullModel[] = [];

  for (const dealer of dealers) {
    let shouldAdd = false;

    for (const application of dealer.applications) {
      if (!filter.areaIds.includes(application.areaId)) {
        continue;
      }

      if (filter.tableTypes && !filter.tableTypes.includes(application.tableType.id)) {
        continue;
      }

      if (filter.addonIds) {
        const hasAddon = application.dealerRequestTypes.some((v) =>
          filter.addonIds!.includes(v.id),
        );

        if (!hasAddon) {
          continue;
        }
      }

      if (!filter.status.includes(application.status)) {
        continue;
      }

      const paid = !!application.paidOrderItemId;

      if (filter.status.includes('paid') && paid) {
        shouldAdd = true;
      } else if (filter.status.includes('unpaid') && !paid) {
        shouldAdd = true;
      }
    }

    if (shouldAdd) {
      result.push(dealer);
    }
  }

  return result;
}

export const DealerSelector: FC<DealerSelectorProps> = ({ selectionChanged }) => {
  const { ts } = useTranslation();
  const [areaIds, setAreaIds] = useState<number[]>([]);
  const [tableTypes, setTableTypes] = useState<number[] | null>(null);
  const [addonIds, setAddonIds] = useState<number[] | null>(null);
  const [status, setStatus] = useState<ApplicationType[]>(['approved', 'paid', 'unpaid']);

  const dealerRequest = useFetcher(async () => {
    if (areaIds.length === 0) {
      return [];
    }

    return await api.getAllDealers(areaIds);
  }, [areaIds]);

  const fetcher = useFetcher(async () => await api.getDealerAreas());

  useEffect(() => {
    const results = filterDealers(dealerRequest.data ?? [], {
      areaIds,
      tableTypes,
      addonIds,
      status,
    });

    selectionChanged(distinct(results.map((t) => t.user.id)));
  }, [dealerRequest.data, areaIds, tableTypes, addonIds, status]);

  if (dealerRequest.loading) {
    return <Fetcher result={dealerRequest} />;
  }

  if (!fetcher.data) {
    return <Fetcher result={fetcher} />;
  }

  return (
    <div>
      <FormGroup>
        <Label for="area" id="dealerAreaSelection">
          {ts('common.areas')}
        </Label>
        {fetcher.data.map((area) => (
          <FormGroup check className="mt-10" key={area.id}>
            <Input
              checked={areaIds.includes(area.id)}
              disabled={!dealerRequest.success}
              id={`area_${area.id}`}
              onChange={(e) => {
                setAreaIds(setElementInArray(areaIds, area.id, e.target.checked));
              }}
              type="checkbox"
            />
            <Label check for={`area_${area.id}`}>
              {area.name}
            </Label>
          </FormGroup>
        ))}
      </FormGroup>
      <FormGroup>
        <Label for="products">{ts('table_type')}</Label>
        <FormGroup check className="mt-10">
          <Input
            checked={tableTypes !== null}
            disabled={!dealerRequest.success}
            id="filterTableType"
            onChange={() => {
              setTableTypes(tableTypes === null ? [] : null);
            }}
            type="checkbox"
          />
          <Label check for="filterTableType">
            {ts('filter_by_table_type')}
          </Label>
        </FormGroup>
        {tableTypes !== null && (
          <TableTypeFilter
            addonIds={addonIds}
            areas={fetcher.data}
            dealers={dealerRequest.data ?? []}
            setAddonIds={setAddonIds}
            setTableTypes={setTableTypes}
            tableTypes={tableTypes}
          />
        )}
      </FormGroup>
      <FormGroup>
        <Label for="dealerStatus">{ts('common.status')}</Label>
        {applicationType.map((type) => {
          return (
            <FormGroup check className="mt-10" key={type}>
              <Input
                checked={status.includes(type)}
                disabled={!dealerRequest.success}
                id={`status_${type}`}
                onChange={(e) => {
                  setStatus(setElementInArray(status, type, e.target.checked));
                }}
                type="checkbox"
              />
              <Label check for={`status_${type}`}>
                {capitalize(type)}
              </Label>
            </FormGroup>
          );
        })}
      </FormGroup>
    </div>
  );
};

interface TableTypeFilterProps {
  readonly areas: DealerArea[];
  readonly dealers: DealerFullModel[];
  readonly tableTypes: number[];
  readonly setTableTypes: (values: number[]) => void;
  readonly addonIds: number[] | null;
  readonly setAddonIds: (values: number[] | null) => void;
}

const TableTypeFilter: FC<TableTypeFilterProps> = ({
  areas,
  dealers,
  tableTypes,
  setTableTypes,
  addonIds,
  setAddonIds,
}) => {
  const categoryIds = useMemo(() => distinct(areas.map((t) => t.categoryId)), [areas]);

  const { ts } = useTranslation();
  const categories = useFetcher(async () => {
    const products: ProductModel[] = [];
    const addons: ProductAddonModel[] = [];

    await Promise.all(
      categoryIds.map(async (categoryId) => {
        const categoryProducts = await api.getFullProductsByCategory(categoryId);

        for (const product of categoryProducts) {
          if (
            !products.some((t) => t.id === product.id) &&
            product.visibility !== ProductVisibility.HIDDEN
          ) {
            products.push(product);
          }

          for (const addon of product.addons) {
            if (!addons.some((t) => t.id === addon.id)) {
              addons.push(addon);
            }
          }
        }
      }),
    );

    return { products, addons };
  }, [categoryIds]);

  return (
    <Fetcher inline result={categories}>
      <>
        <div style={{ paddingLeft: '10px' }}>
          {categories.data?.products.map((product) => {
            const cnt = countBy(dealers, (t) =>
              t.applications.some((u) => u.tableType.id === product.id),
            );

            if (cnt === 0) {
              return null;
            }

            return (
              <FormGroup check className="mt-10" key={product.id}>
                <Input
                  checked={tableTypes.includes(product.id)}
                  id={`product_${product.id}`}
                  onChange={(e) => {
                    setTableTypes(setElementInArray(tableTypes, product.id, e.target.checked));
                  }}
                  type="checkbox"
                />
                <Label check for={`product_${product.id}`}>
                  {displayName(product)} ({cnt})
                </Label>
              </FormGroup>
            );
          })}
        </div>
        <FormGroup check className="mt-10">
          <Input
            checked={addonIds !== null}
            id="filterAddon"
            onChange={() => {
              setAddonIds(addonIds === null ? [] : null);
            }}
            type="checkbox"
          />
          <Label check for="filterAddon">
            {ts('filter_by_addons')}
          </Label>
        </FormGroup>
        <div style={{ paddingLeft: '10px' }}>
          {addonIds !== null &&
            categories.data?.addons.map((addon) => {
              const count = countBy(dealers, (dealer) => {
                return dealer.applications.some((app) =>
                  app.dealerRequestTypes.some((v) => v.id === addon.id),
                );
              });

              if (count === 0) {
                return null;
              }

              return (
                <FormGroup check className="mt-10" key={addon.id}>
                  <Input
                    checked={addonIds.includes(addon.id)}
                    id={`addon_${addon.id}`}
                    onChange={(e) => {
                      setAddonIds(setElementInArray(addonIds, addon.id, e.target.checked));
                    }}
                    type="checkbox"
                  />
                  <Label check for={`addon_${addon.id}`}>
                    {displayName(addon)} ({count})
                  </Label>
                </FormGroup>
              );
            })}
        </div>
      </>
    </Fetcher>
  );
};
